import type { ModalTheme } from '../ModalTheme';
import type { HitTarget, MeasureResult, UiItem } from './Types';
import { Rect } from '../../../engine/hex/types';
import { IconRegistry } from '../../../ui/overlay/assets/IconRegistry';

type LayoutOrientation = 'horizontal' | 'vertical';
type MainGravity = 'start' | 'center' | 'end';
type CrossGravity = 'start' | 'center' | 'end';
type LayoutSize = number | `${number}px` | 'fill' | 'wrap';
type Padding = number | { left: number; top: number; right: number; bottom: number };

export type LinearLayoutStyle = {
    orientation: LayoutOrientation;

    // Size of this container *within parent-provided maxW*.
    width?: LayoutSize; // default: 'fill'
    height?: LayoutSize; // default: 'wrap' (computed)

    // Layout behavior
    gap?: number; // default: theme.spacing.itemGap
    padding?: Padding; // default: 0
    weights?: number[]; // like Android weight; main-axis only
    gravity?: MainGravity; // main-axis alignment if there is extra space
    crossGravity?: CrossGravity; // cross-axis alignment

    // Visuals
    backgroundFill?: string;
    backgroundStroke?: string;
    strokeWidth?: number;
};

function px(v: LayoutSize | undefined): number | 'fill' | 'wrap' {
    if (v === undefined) return 'fill';
    if (v === 'fill' || v === 'wrap') return v;
    if (typeof v === 'number') return v;
    const m = /^(\d+(?:\.\d+)?)px$/.exec(v);
    return m ? Number(m[1]) : 'fill';
}

function pad(p: Padding | undefined): { left: number; top: number; right: number; bottom: number } {
    if (!p) return { left: 0, top: 0, right: 0, bottom: 0 };
    if (typeof p === 'number') return { left: p, top: p, right: p, bottom: p };
    return p;
}

export class LinearLayoutItem implements UiItem {
    private rect: Rect = { x: 0, y: 0, w: 0, h: 0 };

    // caches from measure() used by layout()
    private measuredW = 0;
    private measuredH = 0;
    private childSizes: MeasureResult[] = [];
    private childRects: Rect[] = [];
    private allocatedMainSizes: number[] = []; // allocated widths for horizontal / heights for vertical

    constructor(
        private children: UiItem[],
        private style: LinearLayoutStyle,
    ) {}

    measure(ctx: CanvasRenderingContext2D, maxW: number, theme: ModalTheme): MeasureResult {
        const s = this.style;
        const p = pad(s.padding);
        const gap = s.gap ?? theme.spacing.itemGap;

        const widthSpec = px(s.width);
        const heightSpec = s.height ? px(s.height) : 'wrap';

        // Decide the container width we measure against.
        // For 'wrap' width we still need a working width to measure weighted children;
        // so if weights exist, treat wrap as fill for measurement.
        const hasWeights = s.weights?.some((w) => (w ?? 0) > 0) ?? false;
        const measureW =
            widthSpec === 'fill'
                ? maxW
                : widthSpec === 'wrap'
                  ? hasWeights
                      ? maxW
                      : maxW
                  : Math.min(maxW, widthSpec);

        const innerW = Math.max(0, measureW - p.left - p.right);

        this.childSizes = new Array(this.children.length);
        this.allocatedMainSizes = new Array(this.children.length).fill(0);

        if (s.orientation === 'vertical') {
            // Simple vertical stack: children get innerW as maxW.
            let contentH = 0;
            let maxChildW = 0;

            this.children.forEach((child, i) => {
                const size = child.measure(ctx, innerW, theme);
                this.childSizes[i] = size;
                maxChildW = Math.max(maxChildW, size.w);
                contentH += size.h;
                if (i < this.children.length - 1) contentH += gap;
            });

            const naturalW = p.left + maxChildW + p.right;
            const resolvedW =
                widthSpec === 'fill'
                    ? maxW
                    : widthSpec === 'wrap'
                      ? Math.min(maxW, naturalW)
                      : Math.min(maxW, widthSpec);

            const naturalH = p.top + contentH + p.bottom;
            const resolvedH =
                heightSpec === 'fill'
                    ? naturalH // no parent height provided in interface; treat as wrap here
                    : heightSpec === 'wrap'
                      ? naturalH
                      : Math.max(0, heightSpec);

            this.measuredW = resolvedW;
            this.measuredH = resolvedH;
            return { w: this.measuredW, h: this.measuredH };
        }

        // Horizontal orientation with optional weights (your sizes:[1,2] case).
        // Two-pass: measure fixed children, allocate remaining width to weighted children, then re-measure weighted with allocated width.
        const weights = s.weights ?? [];
        const weightAt = (i: number) => Math.max(0, weights[i] ?? 0);

        // First pass: measure non-weighted children (weight==0) to get their natural sizes.
        let fixedW = 0;
        let maxH = 0;
        let totalWeight = 0;

        this.children.forEach((child, i) => {
            const wgt = weightAt(i);
            if (wgt > 0) {
                totalWeight += wgt;
                this.childSizes[i] = { w: 0, h: 0 }; // placeholder
                return;
            }
            const size = child.measure(ctx, innerW, theme);
            this.childSizes[i] = size;
            fixedW += size.w;
            maxH = Math.max(maxH, size.h);
        });

        const gapsTotal = gap * Math.max(0, this.children.length - 1);
        const remainingW = Math.max(0, innerW - fixedW - gapsTotal);

        // Allocate widths for weighted children.
        if (totalWeight > 0) {
            // integer pixels, stable distribution
            let used = 0;
            const lastWeighted = (() => {
                for (let i = this.children.length - 1; i >= 0; i -= 1)
                    if (weightAt(i) > 0) return i;
                return -1;
            })();

            this.children.forEach((child, i) => {
                const wgt = weightAt(i);
                if (wgt <= 0) {
                    this.allocatedMainSizes[i] = this.childSizes[i].w;
                    return;
                }
                let alloc = Math.floor((remainingW * wgt) / totalWeight);
                if (i === lastWeighted) alloc = remainingW - used; // absorb rounding
                used += alloc;

                this.allocatedMainSizes[i] = alloc;
                const size = child.measure(ctx, alloc, theme);
                this.childSizes[i] = size;
                maxH = Math.max(maxH, size.h);
            });
        } else {
            // No weights: allocated widths are just measured widths
            this.children.forEach((_child, i) => {
                this.allocatedMainSizes[i] = this.childSizes[i].w;
            });
        }

        const contentW = this.allocatedMainSizes.reduce((sum, w) => sum + w, 0) + gapsTotal;
        const naturalW = p.left + contentW + p.right;

        const resolvedW =
            widthSpec === 'fill'
                ? maxW
                : widthSpec === 'wrap'
                  ? Math.min(maxW, naturalW)
                  : Math.min(maxW, widthSpec);

        const naturalH = p.top + maxH + p.bottom;
        const resolvedH =
            heightSpec === 'wrap'
                ? naturalH
                : heightSpec === 'fill'
                  ? naturalH // same limitation as above
                  : Math.max(0, heightSpec);

        this.measuredW = resolvedW;
        this.measuredH = resolvedH;
        return { w: this.measuredW, h: this.measuredH };
    }

    layout(x: number, y: number, w: number, theme: ModalTheme): void {
        const s = this.style;
        const p = pad(s.padding);
        const gap = s.gap ?? theme.spacing.itemGap;

        const widthSpec = px(s.width);
        const selfW =
            widthSpec === 'fill'
                ? w
                : widthSpec === 'wrap'
                  ? Math.min(w, this.measuredW)
                  : Math.min(w, widthSpec);

        // Align the container inside parent-provided (x,w) when selfW < w.
        // Default: start (Android: layout_gravity start).
        let selfX = x;
        if (selfW < w) {
            // keep it simple; you can extend this to selfGravity later
            // (or reuse gravity for self alignment if you want).
            selfX = x; // start
        }

        this.rect = { x: selfX, y, w: selfW, h: this.measuredH };

        const innerX = this.rect.x + p.left;
        const innerY = this.rect.y + p.top;
        const innerW = Math.max(0, this.rect.w - p.left - p.right);
        const innerH = Math.max(0, this.rect.h - p.top - p.bottom);

        this.childRects = new Array(this.children.length);

        if (s.orientation === 'vertical') {
            const gravity: MainGravity = s.gravity ?? 'start';
            const cross: CrossGravity = s.crossGravity ?? 'start';
            const contentH =
                this.childSizes.reduce((sum, size) => sum + (size?.h ?? 0), 0) +
                gap * Math.max(0, this.children.length - 1);
            const extra = Math.max(0, innerH - contentH);

            let cy = innerY;
            if (gravity === 'center') cy = innerY + extra / 2;
            if (gravity === 'end') cy = innerY + extra;

            this.children.forEach((child, i) => {
                const cw = Math.max(0, this.childSizes[i]?.w ?? 0);
                const ch = Math.max(0, this.childSizes[i]?.h ?? 0);

                let cx = innerX;
                let layoutW = innerW;
                if (cross === 'center') {
                    cx = innerX + (innerW - cw) / 2;
                    layoutW = cw;
                }
                if (cross === 'end') {
                    cx = innerX + (innerW - cw);
                    layoutW = cw;
                }

                child.layout(cx, cy, layoutW, theme);
                this.childRects[i] = { x: cx, y: cy, w: layoutW, h: ch };
                cy += ch + (i < this.children.length - 1 ? gap : 0);
            });
            return;
        }

        // Horizontal layout: compute main-axis start based on gravity if extra space exists.
        const gravity: MainGravity = s.gravity ?? 'start';
        const cross: CrossGravity = s.crossGravity ?? 'start';

        const contentW =
            this.allocatedMainSizes.reduce((sum, cw) => sum + cw, 0) +
            gap * Math.max(0, this.children.length - 1);

        const extra = Math.max(0, innerW - contentW);

        let cx = innerX;
        if (gravity === 'center') cx = innerX + extra / 2;
        if (gravity === 'end') cx = innerX + extra;

        this.children.forEach((child, i) => {
            const cw = Math.max(0, this.allocatedMainSizes[i] ?? this.childSizes[i]?.w ?? 0);
            const ch = Math.max(0, this.childSizes[i]?.h ?? 0);

            let cy = innerY;
            if (cross === 'center') cy = innerY + (innerH - ch) / 2;
            if (cross === 'end') cy = innerY + (innerH - ch);

            child.layout(cx, cy, cw, theme);
            this.childRects[i] = { x: cx, y: cy, w: cw, h: ch };

            cx += cw + (i < this.children.length - 1 ? gap : 0);
        });
    }

    draw(ctx: CanvasRenderingContext2D, theme: ModalTheme, icons?: IconRegistry): void {
        // Background first
        if (this.style.backgroundFill || this.style.backgroundStroke) {
            ctx.save();
            if (this.style.backgroundFill) {
                ctx.fillStyle = this.style.backgroundFill;
                ctx.fillRect(this.rect.x, this.rect.y, this.rect.w, this.rect.h);
            }
            if (this.style.backgroundStroke) {
                ctx.strokeStyle = this.style.backgroundStroke;
                ctx.lineWidth = this.style.strokeWidth ?? 1;
                ctx.strokeRect(this.rect.x, this.rect.y, this.rect.w, this.rect.h);
            }
            ctx.restore();
        }

        // Children
        this.children.forEach((child) => child.draw(ctx, theme, icons));
    }

    collectHitTargets(out: HitTarget[], scrollY: number): void {
        this.children.forEach((child) => child.collectHitTargets(out, scrollY));
    }
}
